/**
 * Immobilien – Verstecke (Katalog)
 * /immobilien/verstecke
 *
 * NT-VIERTEL-03-SCHALTZENTRALE (F7): reiner Katalog ohne Kaufweg — nur noch Profil/Preise/Specs
 * zum Browsen. Kaufen, Bebauen, Umziehen, Verkaufen und Öffnen laufen jetzt ausschließlich im Tab
 * VIERTEL (Crime Ops), das die tatsächliche Adresse/Fläche der Parzelle kennt. Bereits gekaufte
 * Verstecke werden über Crime Ops → IMMOBILIEN verwaltet, nicht mehr hier.
 */

import { api } from '../../services/api.js';
import { navigate } from '../../router.js';

// ─── Typen ────────────────────────────────────────────────────────────────────

interface CatalogAxisRange {
  base: number;
  max:  number;
}

interface CatalogHideout {
  subType:            string;
  label:              string;
  purchasable:        boolean;
  storage:            CatalogAxisRange;
  camouflage:         CatalogAxisRange;
  garage:             CatalogAxisRange;
  minFlaecheQm:       number;
  raidRiskPerFL:      number;
  purchasePriceCents: string;
  rentPerWeekCents:   string;
  lore:               string;
}

// ─── Katalog: Max-Ausbaustufen je subType (lokal — kein Server-Lookup nötig) ──
// Backend: hideout.definitions.ts::MAX_GROW_LEVELS/MAX_LABOR_LEVELS (kein Shared-Import für
// reine Anzeige-Werte, etabliertes Repo-Pattern).

const MAX_GROW: Record<string, number> = {
  dachboden: 2, hinterzimmer: 2, wohnung: 3, kellerstudio: 2, gartenlaube: 3,
  campingwagen: 2, bungalow: 3, luxus_apartment: 3, lagercontainer: 2, bootshaus: 3, bunker: 3,
  // NT-HIDEOUT-PROFILE-01 — neu
  waschkeller: 1, kirchturm: 2, tiefgaragenabteil: 0, schrebergarten: 3,
  doenerbude: 0, kegelbahn: 3, tankstelle: 0, sparkassenfiliale: 0,
};
const MAX_LABOR: Record<string, number> = {
  dachboden: 1, hinterzimmer: 2, wohnung: 2, kellerstudio: 3, gartenlaube: 1,
  campingwagen: 2, bungalow: 3, luxus_apartment: 3, lagercontainer: 3, bootshaus: 2, bunker: 3,
  // NT-HIDEOUT-PROFILE-01 — neu
  waschkeller: 2, kirchturm: 0, tiefgaragenabteil: 0, schrebergarten: 0,
  doenerbude: 3, kegelbahn: 1, tankstelle: 2, sparkassenfiliale: 0,
};

// ─── State ────────────────────────────────────────────────────────────────────

let catalogData: CatalogHideout[] | null = null;
let loadError:   string | null = null;

// ─── Helfer ───────────────────────────────────────────────────────────────────

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function fmtNox(cents: string | number): string {
  const n = typeof cents === 'string' ? parseInt(cents, 10) : cents;
  return (n / 100).toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' ₦';
}

// ─── Render: Catalog Card ─────────────────────────────────────────────────────

function renderCatalogCard(h: CatalogHideout): string {
  const maxGrow  = MAX_GROW[h.subType] ?? 0;
  const maxLabor = MAX_LABOR[h.subType] ?? 0;

  // NT-HIDEOUT-PROFILE-01: Profil statt Preisleiter — LAGER/TARNUNG/GARAGE als Basis→Max,
  // "MAX 0" wird als "—" statt einer verwirrenden "0→0"-Zeile gezeigt.
  const garageLabel = h.garage.max === 0 ? '—' : `${String(h.garage.base)}→${String(h.garage.max)}`;
  const camouflageLabel = h.camouflage.max === 0 ? '—' : `MAX&nbsp;${String(h.camouflage.max)}`;

  return `
    <div class="vstk-catalog-card">
      <div class="vstk-catalog-header">
        <span class="vstk-catalog-title">${esc(h.label.toUpperCase())}</span>
        <span class="vstk-catalog-slots">${String(h.storage.base)}→${String(h.storage.max)} SLOTS</span>
      </div>
      <p class="vstk-catalog-lore">${esc(h.lore)}</p>
      <div class="vstk-catalog-meta">
        <span class="vstk-catalog-price">${fmtNox(h.purchasePriceCents)}</span>
        <span class="vstk-meta-item">MIETE&nbsp;${fmtNox(h.rentPerWeekCents)}/W</span>
        <span class="vstk-meta-item" title="pro Tag bei vollem Verdacht">RAZZIA&nbsp;${String(Math.round(h.raidRiskPerFL * 100))}%/FL</span>
        <span class="vstk-meta-item"><span style="color:var(--text-muted);">TARNUNG</span>&nbsp;<span style="color:var(--text-primary);">${camouflageLabel}</span></span>
        <span class="vstk-meta-item">GROW&nbsp;MAX&nbsp;S${String(maxGrow)}</span>
        <span class="vstk-meta-item">LABOR&nbsp;MAX&nbsp;S${String(maxLabor)}</span>
        <span class="vstk-meta-item"><span style="color:var(--text-muted);">FAHRZEUGPLÄTZE</span>&nbsp;<span style="color:var(--text-primary);">${garageLabel}</span></span>
        <span class="vstk-meta-item"><span style="color:var(--text-muted);">FLÄCHE&nbsp;AB</span>&nbsp;<span style="color:var(--text-primary);">${String(h.minFlaecheQm)}&nbsp;m²</span></span>
      </div>
    </div>
  `;
}

// ─── Render: Vollständige Seite ───────────────────────────────────────────────

function renderPage(): string {
  let catalogHtml: string;
  if (loadError) {
    catalogHtml = `<p class="vstk-msg vstk-msg--error">FEHLER: ${esc(loadError)}</p>`;
  } else if (catalogData === null) {
    catalogHtml = '<p class="vstk-msg">LADE...</p>';
  } else {
    catalogHtml = catalogData.filter((h) => h.purchasable).map(renderCatalogCard).join('');
  }

  return `
    <section class="dash-content" id="vstk-root" style="padding:0;">
      <div class="vstk-page-header">
        <div>
          <h2 class="vstk-page-title">VERSTECKE</h2>
          <p class="vstk-page-sub">Ausbaubar zu Grow Room oder Labor</p>
        </div>
      </div>

      <div class="vstk-section">
        <p class="vstk-viertel-hint">
          Kaufen, Umziehen und Verkaufen laufen jetzt im Viertel — dort siehst du auch, welche
          Fläche eine Parzelle hat und was darauf gebaut werden darf.
        </p>
        <button class="vstk-btn vstk-btn--buy" data-vstk-zum-viertel>ZUM VIERTEL</button>
      </div>

      <div class="vstk-section">
        <h3 class="vstk-section-title">SORTIMENT</h3>
        <div class="vstk-catalog-list">${catalogHtml}</div>
      </div>
    </section>
  `;
}

// ─── Event-Handling ───────────────────────────────────────────────────────────

function rerender(): void {
  const root = document.getElementById('vstk-root');
  if (!root) return;
  const tmp = document.createElement('div');
  tmp.innerHTML = renderPage();
  const newRoot = tmp.firstElementChild;
  if (newRoot) root.replaceWith(newRoot);
  attachListeners();
}

function attachListeners(): void {
  const root = document.getElementById('vstk-root');
  if (!root) return;

  root.addEventListener('click', (e) => {
    const target = e.target as Element;
    if (target.closest('[data-vstk-zum-viertel]')) {
      sessionStorage.setItem('crimeops_tab', 'viertel');
      navigate('/business/crime-ops');
    }
  });
}

async function reload(): Promise<void> {
  try {
    const result = await api.get<{ hideouts: CatalogHideout[] }>('/properties/catalog');
    catalogData = result.hideouts;
    loadError = null;
  } catch (err) {
    loadError = err instanceof Error ? err.message : 'Unbekannter Fehler';
  }
  rerender();
}

// ─── Styles ───────────────────────────────────────────────────────────────────

function addStyles(): void {
  if (document.getElementById('vstk-styles')) return;
  const s = document.createElement('style');
  s.id = 'vstk-styles';
  s.textContent = `
    /* Seiten-Header */
    .vstk-page-header{display:flex;align-items:flex-start;justify-content:space-between;
      padding:var(--space-3) var(--space-4) var(--space-2);border-bottom:1px solid var(--border);}
    .vstk-page-title{font-family:var(--font-mono);font-size:14px;letter-spacing:.12em;
      color:var(--accent-gold);text-transform:uppercase;margin:0 0 2px;}
    .vstk-page-sub{font-family:var(--font-mono);font-size:var(--font-size-xs);
      color:var(--text-muted);margin:0;}

    /* Sektions-Header */
    .vstk-section{padding:var(--space-4);}
    .vstk-section-title{font-family:var(--font-mono);font-size:var(--font-size-xs);
      letter-spacing:.1em;color:var(--text-muted);text-transform:uppercase;
      margin:0 0 var(--space-3);}
    .vstk-viertel-hint{font-family:var(--font-mono);font-size:var(--font-size-xs);
      color:var(--text-muted);margin:0 0 var(--space-3);line-height:1.5;}

    /* Meta-Zeile (Katalog) */
    .vstk-meta-item{font-family:var(--font-mono);font-size:12px;
      color:var(--text-muted);white-space:nowrap;}

    /* Buttons */
    .vstk-btn{font-family:var(--font-mono);font-size:var(--font-size-xs);
      letter-spacing:.07em;text-transform:uppercase;padding:7px 16px;
      background:transparent;border-radius:var(--radius-btn);cursor:pointer;
      min-height:36px;border:1px solid var(--border);color:var(--text-muted);
      transition:color var(--transition-fast),border-color var(--transition-fast);}
    .vstk-btn--buy{border-color:var(--accent-green);color:var(--accent-green);}
    @media(max-width:480px){.vstk-btn{width:100%;}}

    /* Katalog */
    .vstk-catalog-list{display:flex;flex-direction:column;
      border:1px solid var(--border);border-radius:var(--radius);overflow:hidden;}
    .vstk-catalog-card{padding:var(--space-3) var(--space-4);
      background:var(--bg-surface);border-bottom:1px solid var(--border);}
    .vstk-catalog-card:last-child{border-bottom:none;}
    .vstk-catalog-header{display:flex;justify-content:space-between;
      align-items:baseline;margin-bottom:4px;gap:var(--space-2);}
    .vstk-catalog-title{font-family:var(--font-mono);font-size:var(--font-size-base);
      color:var(--text-primary);letter-spacing:.05em;}
    .vstk-catalog-slots{font-family:var(--font-mono);font-size:var(--font-size-sm);
      color:var(--accent-gold);}
    .vstk-catalog-lore{font-family:var(--font-mono);font-size:12px;
      color:var(--text-italic);font-style:italic;line-height:1.5;
      margin:0 0 var(--space-2);}
    .vstk-catalog-meta{display:flex;flex-wrap:wrap;gap:var(--space-3);
      align-items:baseline;margin-bottom:0;}
    .vstk-catalog-price{font-family:var(--font-mono);font-size:var(--font-size-sm);
      color:var(--accent-gold);letter-spacing:.03em;}

    /* Sonstige */
    .vstk-msg{font-family:var(--font-mono);font-size:12px;
      color:var(--text-muted);padding:var(--space-4) 0;}
    .vstk-msg--error{color:var(--accent-red);}
  `;
  document.head.appendChild(s);
}

// ─── Lifecycle ────────────────────────────────────────────────────────────────

/** Initiales HTML-Skelett (vor dem ersten Laden) */
export function renderVersteckeTab(): string {
  return `
    <section class="dash-content" style="padding:0;" id="vstk-root">
      <p style="font-family:var(--font-mono);font-size:12px;color:var(--text-muted);
        text-align:center;padding:var(--space-8) 0;letter-spacing:.08em;">LADE...</p>
    </section>
  `;
}

/** Aufgerufen wenn die Seite angezeigt wird */
export function mountVersteckeTab(): void {
  addStyles();
  void reload();
}

/** Aufgerufen wenn die Seite verlassen wird */
export function cleanupVerstecke(): void {
  catalogData = null;
  loadError   = null;
}
